export type LinkedInNetworkDistance = 'FIRST_DEGREE' | 'SECOND_DEGREE' | 'THIRD_DEGREE' | 'OUT_OF_NETWORK';

/**
 * classic = sentinelle "pas de capacité InMail configurée"
 */
export enum LinkedInApiProduct {
  recruiter = 'recruiter',
  sales_navigator = 'sales_navigator',
  classic = 'classic',
}

export type LinkedInMessageSent = {
  chatId: string;
  messageId: string;
};

/**
 * Longueur maximale d'une note d'invitation imposée par LinkedIn.
 */
export const LINKEDIN_INVITATION_NOTE_MAX_LENGTH = 300;

/**
 * Une demande de connexion n'ouvre pas de conversation : ni chatId ni messageId.
 */
export type LinkedInInvitationSent = {
  invitationId: string;
};

export class LinkedInProfileNotFoundError extends Error {
  extensions: { code: 'LINKEDIN_PROFILE_NOT_FOUND'; talentLinkedInSlug: string };

  constructor(message: string, talentLinkedInSlug: string) {
    super(message);
    this.name = 'LinkedInProfileNotFoundError';
    this.extensions = { code: 'LINKEDIN_PROFILE_NOT_FOUND', talentLinkedInSlug };
    Object.setPrototypeOf(this, LinkedInProfileNotFoundError.prototype);
  }
}

export type LinkedInMessagingProviderErrorDetails = {
  message: string;
  statusCode?: number;
};

export class LinkedInMessagingProviderError extends Error {
  extensions: { code: 'LINKEDIN_MESSAGING_PROVIDER_ERROR' } & LinkedInMessagingProviderErrorDetails;

  constructor(message: string, details: LinkedInMessagingProviderErrorDetails) {
    super(message);
    this.name = 'LinkedInMessagingProviderError';
    this.extensions = { code: 'LINKEDIN_MESSAGING_PROVIDER_ERROR', ...details };
    Object.setPrototypeOf(this, LinkedInMessagingProviderError.prototype);
  }
}

export class LinkedInSendMessageValidationError extends Error {
  extensions: { code: 'LINKEDIN_SEND_MESSAGE_VALIDATION_ERROR' };

  constructor(message: string) {
    super(message);
    this.name = 'LinkedInSendMessageValidationError';
    this.extensions = { code: 'LINKEDIN_SEND_MESSAGE_VALIDATION_ERROR' };
    Object.setPrototypeOf(this, LinkedInSendMessageValidationError.prototype);
  }
}

export class LinkedInNotFirstDegreeError extends Error {
  extensions: { code: 'LINKEDIN_NOT_FIRST_DEGREE'; networkDistance: Exclude<LinkedInNetworkDistance, 'FIRST_DEGREE'> };

  constructor(talentLinkedInSlug: string, networkDistance: Exclude<LinkedInNetworkDistance, 'FIRST_DEGREE'>) {
    super(`Le talent ${talentLinkedInSlug} n'est pas en relation de 1er degré (${networkDistance})`);
    this.name = 'LinkedInNotFirstDegreeError';
    this.extensions = { code: 'LINKEDIN_NOT_FIRST_DEGREE', networkDistance };
    Object.setPrototypeOf(this, LinkedInNotFirstDegreeError.prototype);
  }
}

export class LinkedInInMailInsufficientCreditsError extends Error {
  extensions: { code: 'LINKEDIN_INMAIL_INSUFFICIENT_CREDITS' };

  constructor(message: string) {
    super(message);
    this.name = 'LinkedInInMailInsufficientCreditsError';
    this.extensions = { code: 'LINKEDIN_INMAIL_INSUFFICIENT_CREDITS' };
    Object.setPrototypeOf(this, LinkedInInMailInsufficientCreditsError.prototype);
  }
}

export class LinkedInInMailInvalidAccountError extends Error {
  extensions: { code: 'LINKEDIN_INMAIL_INVALID_ACCOUNT'; linkedinApi: LinkedInApiProduct };

  constructor(message: string, linkedinApi: LinkedInApiProduct) {
    super(message);
    this.name = 'LinkedInInMailInvalidAccountError';
    this.extensions = { code: 'LINKEDIN_INMAIL_INVALID_ACCOUNT', linkedinApi };
    Object.setPrototypeOf(this, LinkedInInMailInvalidAccountError.prototype);
  }
}

export class LinkedInSendInvitationValidationError extends Error {
  extensions: { code: 'LINKEDIN_SEND_INVITATION_VALIDATION_ERROR' };

  constructor(message: string) {
    super(message);
    this.name = 'LinkedInSendInvitationValidationError';
    this.extensions = { code: 'LINKEDIN_SEND_INVITATION_VALIDATION_ERROR' };
    Object.setPrototypeOf(this, LinkedInSendInvitationValidationError.prototype);
  }
}

export class LinkedInInvitationNoteTooLongError extends Error {
  extensions: { code: 'LINKEDIN_INVITATION_NOTE_TOO_LONG'; maxLength: number; actualLength: number };

  constructor(actualLength: number) {
    super(`La note d'invitation dépasse ${LINKEDIN_INVITATION_NOTE_MAX_LENGTH} caractères (${actualLength})`);
    this.name = 'LinkedInInvitationNoteTooLongError';
    this.extensions = {
      code: 'LINKEDIN_INVITATION_NOTE_TOO_LONG',
      maxLength: LINKEDIN_INVITATION_NOTE_MAX_LENGTH,
      actualLength,
    };
    Object.setPrototypeOf(this, LinkedInInvitationNoteTooLongError.prototype);
  }
}

export class LinkedInAlreadyInRelationError extends Error {
  extensions: { code: 'LINKEDIN_ALREADY_IN_RELATION'; talentLinkedInSlug: string };

  constructor(talentLinkedInSlug: string) {
    super(
      `Le talent ${talentLinkedInSlug} est déjà en relation de 1er degré : envoyer un message plutôt qu'une invitation`,
    );
    this.name = 'LinkedInAlreadyInRelationError';
    this.extensions = { code: 'LINKEDIN_ALREADY_IN_RELATION', talentLinkedInSlug };
    Object.setPrototypeOf(this, LinkedInAlreadyInRelationError.prototype);
  }
}

export class LinkedInInvitationAlreadySentError extends Error {
  extensions: { code: 'LINKEDIN_INVITATION_ALREADY_SENT' };

  constructor(message: string) {
    super(message);
    this.name = 'LinkedInInvitationAlreadySentError';
    this.extensions = { code: 'LINKEDIN_INVITATION_ALREADY_SENT' };
    Object.setPrototypeOf(this, LinkedInInvitationAlreadySentError.prototype);
  }
}

export class LinkedInInvitationLimitReachedError extends Error {
  extensions: { code: 'LINKEDIN_INVITATION_LIMIT_REACHED' };

  constructor(message: string) {
    super(message);
    this.name = 'LinkedInInvitationLimitReachedError';
    this.extensions = { code: 'LINKEDIN_INVITATION_LIMIT_REACHED' };
    Object.setPrototypeOf(this, LinkedInInvitationLimitReachedError.prototype);
  }
}

export interface ILinkedInMessagingService {
  getNetworkDistance: (accountId: string, talentLinkedInSlug: string) => Promise<LinkedInNetworkDistance>;

  /**
   * Orchestrateur : résout le degré de relation, puis choisit automatiquement le mécanisme
   * d'envoi. linkedinApi === LinkedInApiProduct.classic signifie "ce compte n'a pas de capacité InMail".
   */
  sendLinkedInMessage: (
    sender: { accountId: string; linkedinApi: LinkedInApiProduct },
    talentLinkedInSlug: string,
    text: string,
  ) => Promise<LinkedInMessageSent>;

  sendLinkedInInvitation: (
    accountId: string,
    talentLinkedInSlug: string,
    note: string,
  ) => Promise<LinkedInInvitationSent>;
}
